import React from 'react';
import { Alert, Typography, Row, Col, theme, Button, Collapse, CollapseProps } from 'antd';
import { InfoCircleOutlined, BulbOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';

import { useSelector } from '../../hooks/usePrerenderUser';
import SetupChecklistProgress from './SetupChecklistProgress';
import { type SetupChecklistUpdateCompany } from './useSetupChecklistState';
import getSetupChecklistCollapsibleItem from './getSetupChecklistCollapsibleItem';
import useTrackSetupChecklistEvent from './useTrackChecklistEvent';
import {
  SETUP_CHECKLIST_KEYS,
  STATE_TO_KEY_MAP,
  type SetupChecklistKey,
  type SetupChecklistStateCompany,
} from './SetupChecklist';
import useSetupChecklist from './useSetupChecklist';

const RELEASE_DATE = '2026-01-20';

function getFirstIncompleteState(state: SetupChecklistStateCompany): SetupChecklistKey | undefined {
  return Object.keys(state).find((key) => !state[key as keyof SetupChecklistStateCompany]) as
    | SetupChecklistKey
    | undefined;
}

function stateToArrayOfKeys(key?: string): SetupChecklistKey[] {
  return key ? [STATE_TO_KEY_MAP[key as keyof SetupChecklistStateCompany]] : [];
}

function SetupChecklistContainer() {
  const { token } = theme.useToken();
  const { setupChecklistState, updateSetupChecklistState, remainingItemsCount } = useSetupChecklist();
  const trackSetupChecklistEvent = useTrackSetupChecklistEvent();
  const navigate = useNavigate();
  const companyCreatedAt = useSelector((state) => state.prerenderUser.companyCreationDate);
  const trackingEventSent = React.useRef(false);

  const isOldCompany = companyCreatedAt < new Date(RELEASE_DATE);

  const checklistKeys = Object.values(SETUP_CHECKLIST_KEYS);
  const [activeKey, setActiveKey] = React.useState<SetupChecklistKey[]>(
    stateToArrayOfKeys(getFirstIncompleteState(setupChecklistState))
  );

  const markAsDone = (patch: SetupChecklistUpdateCompany['state']) => {
    if (activeKey.length > 0) {
      trackSetupChecklistEvent('ItemMarkedAsDone', { item_name: activeKey[0] });
    }

    const newState = { ...setupChecklistState, ...patch };
    const firstIncompleteState = getFirstIncompleteState(newState);
    if (firstIncompleteState) {
      setActiveKey(stateToArrayOfKeys(firstIncompleteState));
    } else {
      trackSetupChecklistEvent('AllItemsMarkedAsDone');
      setActiveKey([]);
    }

    updateSetupChecklistState(patch);
  };

  const items: CollapseProps['items'] = getSetupChecklistCollapsibleItem(setupChecklistState, activeKey, markAsDone);

  const handleInviteClick = () => {
    trackSetupChecklistEvent('InviteClicked');
    navigate('/security/team-members');
  };

  React.useEffect(() => {
    if (!trackingEventSent.current) {
      trackSetupChecklistEvent('PageViewed', { is_completed: remainingItemsCount === 0 });
      trackingEventSent.current = true;
    }
  }, [remainingItemsCount]);

  return (
    <div style={{ maxWidth: token.screenXL, margin: '0 auto', padding: token.paddingLG }}>
      <Row gutter={token.marginLG}>
        <Col xs={24} sm={24} md={24} lg={18} span={18}>
          <div style={{ textAlign: 'center' }}>
            <Typography.Title level={2}>Your One-Time Optimization Checklist</Typography.Title>
          </div>

          <Alert
            showIcon
            style={{
              background: '#fafafa',
              border: '1px solid #f0f0f0',
            }}
            icon={<InfoCircleOutlined style={{ color: '#8c8c8c' }} />}
            message="What's this?"
            description={
              <Typography.Text type="secondary">
                This one-time checklist helps your team optimize Prerender for better SEO and cost efficiency. Complete
                the steps to make the most out of Prerender, save on costs, and improve your visibility. Anyone on the
                team can contribute—complete remaining items or review what&apos;s already configured.
              </Typography.Text>
            }
          />

          {isOldCompany && (
            <Alert
              showIcon
              style={{
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                marginTop: token.marginMD,
              }}
              icon={<BulbOutlined style={{ color: '#fadb14' }} />}
              message="Already configured some of this?"
              description={
                <Typography.Text type="secondary">
                  You may have already completed some of these steps. Use this checklist as an audit to verify your
                  setup is optimized, or to discover features you haven&apos;t configured yet. Mark items as done as you
                  review them.
                </Typography.Text>
              }
            />
          )}

          <div style={{ marginTop: token.marginMD, marginBottom: token.marginMD }}>
            <SetupChecklistProgress
              totalItems={checklistKeys.length}
              completedItems={checklistKeys.length - remainingItemsCount}
            />
          </div>

          <Collapse
            accordion
            bordered={false}
            expandIconPosition="end"
            items={items}
            style={{ background: '#f5f5f5' }}
            onChange={(key) => setActiveKey(key as SetupChecklistKey[])}
            activeKey={activeKey}
          />

          {remainingItemsCount > 0 && (
            <div style={{ textAlign: 'center' }}>
              <Link
                to="/"
                style={{ width: '100%', color: token.colorPrimary }}
                onClick={() => trackSetupChecklistEvent('SkipForNow')}
              >
                Skip for now
              </Link>
            </div>
          )}
        </Col>
        <Col xs={24} sm={24} md={24} lg={6} span={6}>
          <div>
            <Typography.Paragraph strong>Working with a team?</Typography.Paragraph>
            <Typography.Paragraph type="secondary">
              Invite others to help you optimise your Prerender account.
            </Typography.Paragraph>
            <Button block onClick={handleInviteClick}>
              Invite Team Member
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default SetupChecklistContainer;
